import { z } from "zod";

// Story 15.1 AC12/AC13 — the Screen 33 provisioning form contract.
//
// ELEVEN fields: the nine FR74 commercial fields MINUS `org_type`, plus the first
// org_admin's email, first name and last name. Follows the `create-user-schema.ts`
// shape — trimmed strings, server-mirrored bounds, one message per refusal.
//
// `org_type` IS ABSENT, not pinned to a literal here. The request schema
// (`create_organization/schemas.py`) does not accept the field at all, so a value
// in this object would be one the wire never carries — the form renders the type
// as read-only text instead of a control (boundary row 9 / AC11).
//
// THE +2 DELTA vs. the mockup: `adminFirstName` and `adminLastName` do not exist on
// Screen 33. Without them the account renders nameless in the Story 11.1 Admin
// Console list and in 15.6's member deep-link, and both invite forms already in the
// repo require both names (boundary row 8).
//
// THE `(optional)` MARKER IS GONE from the first-admin field. Under variant A the
// organization's `owner_user_id` is NOT NULL from the first instant the row exists,
// so there is no create-without-an-owner path for the field to be optional for; a
// form still offering to skip it would misstate its own contract (boundary row 7).

// Mirrors the backend `CreateOrganizationRequest` field-for-field: 255 for the name
// and the CRM key, 1000 for the free-text address, 100 for the admin's names, and
// the RFC 5321 ceiling of 254 for every address.
const NAME_MAX = 255;
const REFERENCE_MAX = 255;
const ADDRESS_MAX = 1000;
const PERSON_NAME_MAX = 100;
const EMAIL_MAX = 254;

// `adjust_seats/schemas.py :: AdjustSeatsRequest`, copied deliberately: the two
// write the same column, and the int32 ceiling is the Postgres column limit — an
// oversized value passes a bare `positive()` here and dies at flush as a raw 500.
//
// NOT A COMMERCIAL BOUND, despite the name. `SEAT_CAPACITY_MAX` is the column
// type's ceiling and nothing else: no policy says how large a pool CITS may sell,
// so a super_admin can provision a two-billion-seat organization and this form
// will accept it. The only thing the bound buys is a field error instead of a 500.
// A real sold-capacity cap would be a product decision nobody has taken; if one
// ever arrives it belongs beside it as its own constant, and this one stays where
// it is so the raw-500 guard survives the policy change.
export const SEAT_CAPACITY_MIN = 1;
export const SEAT_CAPACITY_MAX = 2_147_483_647;

/** Rendered read-only beside the label; never submitted (boundary row 9). */
export const CREATE_ORGANIZATION_ORG_TYPE = "professional" as const;

const requiredText = (max: number, missing: string, tooLong: string) =>
  z.string().trim().min(1, missing).max(max, tooLong);

const requiredEmail = (missing: string) =>
  z
    .string()
    .trim()
    .min(1, missing)
    .max(EMAIL_MAX, `Email must be ${EMAIL_MAX} characters or fewer.`)
    .email("Enter a valid email address.");

// An <input type="date"> yields "" when empty and "YYYY-MM-DD" otherwise, which is
// already the wire format — so the schema validates the shape rather than parsing to
// a Date and re-serialising it, which would drag the browser's timezone into a value
// the server stores as a bare calendar date.
export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const requiredDate = (missing: string) =>
  z.string().trim().min(1, missing).regex(ISO_DATE, "Enter a valid date.");

export const createOrganizationSchema = z.object({
  name: requiredText(
    NAME_MAX,
    "Enter the legal organization name.",
    `Name must be ${NAME_MAX} characters or fewer.`,
  ),
  // OPTIONAL AT CREATE (Story 15.6). The CRM key is not always in the operator's
  // hand at provisioning time — CITS allocates it on its own schedule — and the
  // column is write-once, so demanding it HERE forces the operator to either wait
  // or invent a placeholder that can then never be corrected. Screen 24's Contract
  // card is the second, unhurried chance to set it.
  //
  // Still a plain `string` after parse (no `.optional()`, no `undefined` in the
  // inferred type): the control is an `<input>` whose empty value is `""`, and a
  // union type here would cost the form a cast on every `defaultValues` entry.
  // Only the `.min(1)` requirement is dropped; the 255 ceiling is unchanged.
  externalReferenceId: z
    .string()
    .trim()
    .max(REFERENCE_MAX, `External reference ID must be ${REFERENCE_MAX} characters or fewer.`),
  billingAddress: requiredText(
    ADDRESS_MAX,
    "Enter the billing address.",
    `Billing address must be ${ADDRESS_MAX} characters or fewer.`,
  ),
  // Validated as the STRING the control actually holds, and converted at submit.
  // `z.coerce.number()` would make the parsed type `number` while the field's real
  // value is `""` until the operator types, which costs a cast on `defaultValues`
  // and reports an empty field as Zod's "Expected number, received nan".
  seatCapacity: z
    .string()
    .trim()
    .min(1, "Enter a seat capacity of 1 or more.")
    .regex(/^-?\d+$/, "Enter a whole number of seats.")
    .refine((v) => Number(v) >= SEAT_CAPACITY_MIN, "Enter a seat capacity of 1 or more.")
    .refine((v) => Number(v) <= SEAT_CAPACITY_MAX, "That seat capacity is too large."),
  contractStartDate: requiredDate("Enter the contract start date."),
  contractEndDate: requiredDate("Enter the contract end date."),
  billingContactEmail: requiredEmail("Enter the billing contact email address."),
  securityContactEmail: requiredEmail("Enter the security contact email address."),
  adminEmail: requiredEmail("Enter the first organization admin's email address."),
  adminFirstName: requiredText(
    PERSON_NAME_MAX,
    "Enter the first organization admin's first name.",
    `First name must be ${PERSON_NAME_MAX} characters or fewer.`,
  ),
  adminLastName: requiredText(
    PERSON_NAME_MAX,
    "Enter the first organization admin's last name.",
    `Last name must be ${PERSON_NAME_MAX} characters or fewer.`,
  ),
});

export type CreateOrganizationValues = z.infer<typeof createOrganizationSchema>;
